/**
 * AlertBoardPage (shared) — the operator's alert board, in any of its three presentations,
 * behind a toggle in the toolbar.
 *
 * The honeycomb (every camera, clustered site → group), the GIS board (cameras pinned on a
 * floor plan) and the map (cameras at their real-world coordinates) were separate pages
 * showing the SAME live data through the same `useAlertBoard` engine, the same colour
 * language and the same docked alert panel. A sidebar entry per board meant an operator who
 * wanted a different read of an alert had to leave the page and lose their position; one page
 * with a toggle makes it one click in place.
 *
 * WHAT EACH ONE ANSWERS — which is why there are three and not fewer:
 *   · Honeycomb — "which camera", every camera at once, nothing spatial
 *   · GIS       — "which door", pinned on a plan of the premises
 *   · Map       — "which building, which street", pinned on OpenStreetMap
 *
 * The board formerly LABELLED "Map View" is the floor-plan one; it is now GIS View, and the
 * name Map View belongs to the geographic board, where it means what an operator expects.
 *
 * Only the LAYOUT swaps. Which board is on screen is a presentation choice, not a different
 * surface, so the toggle is a segmented control in the CENTRE of each board's toolbar rather
 * than tabs — dead centre because it is the one control on the bar that belongs to no board,
 * and the page's own name is not repeated there (the sidebar and title bar say it).
 *
 * The boards are mounted exclusively, never all-but-one-hidden: each holds live WebRTC
 * subscriptions and its own realtime watch, and paying for the idle ones so they stay warm
 * would multiply every stream on the page.
 *
 * Node-agnostic like the boards it wraps: `alertsApi` / `mapApi` / `geoApi` and the video
 * player are injected, so local passes SFU wiring and cloud passes its per-node tunnel wiring.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GlobeAltIcon, MapPinIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import AlertHexagonPage from './AlertHexagonPage';
import CameraMapPage from './CameraMapPage';
import CameraGeoMapPage from './CameraGeoMapPage';
import SegmentedToggle, { type SegmentedOption } from '../components/common/SegmentedToggle';
import type { HexPlayerComponent, ViewAlertRequest } from '../components/hexagon/types';
import type { GeoMapApi, HexAlertsApi, MapLayoutApi } from '../api/contracts';

/** `gis` is the floor-plan board (it was called `map` before the geographic board existed);
 *  `map` is now the geographic one. See the storage-key note below — the rename is not free. */
export type BoardView = 'hex' | 'gis' | 'map';

/**
 * VERSIONED on purpose. Under the previous key a stored `'map'` meant the FLOOR PLAN, and
 * reusing it would open the geographic board for every operator who had last used the plan —
 * silently, and on the one board they had not asked for. A new key simply starts them on the
 * honeycomb once.
 */
const STORAGE_KEY = 'trinetra.alertboard.view.v2';

// Symbol + name. The icons are the sidebar's own — the hexagon board and Map View carried
// these before the merge, so an operator recognises them from the menu they used to be in.
// The tooltip says what each view IS, which neither the name nor the symbol quite manages.
const VIEW_OPTIONS: Record<BoardView, SegmentedOption<BoardView>> = {
  hex: {
    key: 'hex',
    label: 'Honeycomb View',
    icon: RectangleGroupIcon,
    title: 'Every camera, clustered by site',
  },
  gis: {
    key: 'gis',
    label: 'GIS View',
    icon: MapPinIcon,
    title: 'Cameras pinned on a floor plan',
  },
  map: {
    key: 'map',
    label: 'Map View',
    icon: GlobeAltIcon,
    title: 'Cameras at their real-world locations',
  },
};

export interface AlertBoardPageProps {
  /** Realtime alerts + camera roster. Backs ALL THREE boards. */
  alertsApi: HexAlertsApi;
  /** Floor-plan layouts and pin placements. Only the GIS board reads it. */
  mapApi: MapLayoutApi;
  /** Camera coordinates + the saved default view. Only the geographic board reads it.
   *  Optional so a caller that pins the board to another view needn't wire it. */
  geoApi?: GeoMapApi;
  PlayerComponent: HexPlayerComponent;
  /** Caller holds `map.edit` — shows the layout, pin and default-view editing affordances on
   *  both spatial boards. */
  canEdit?: boolean;
  /** Cloud: require a selected node; the boards show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → both boards reload. */
  nodeKey?: string;
  className?: string;
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * This board covers ONE site — the honeycomb draws every group on a single shared
   * background instead of a panel per site. Local passes it (the node IS the site); cloud
   * leaves it off, where each node genuinely is a different place.
   */
  singleSite?: boolean;

  /**
   * Which views this user may see, in toolbar order. Defaults to all three.
   *
   * The boards carry different RBAC keys (`alerts.view` for the honeycomb, `map.view` for both
   * spatial boards), and merging the pages must not merge the permissions — a viewer entitled
   * to only one of them gets that one, with no toggle at all rather than a button that bounces
   * them to the dashboard.
   *
   * The two spatial boards deliberately SHARE `map.view`: they are one entitlement — "see
   * where my cameras are" — drawn on two backdrops, and splitting the key would withhold the
   * new board from every role that already holds the old one.
   */
  allowedViews?: BoardView[];

  /**
   * The current view. Controlled, because each app keeps it in the URL — that's what makes
   * the merged board deep-linkable, bookmarkable and able to light up the right sidebar item.
   */
  view: BoardView;
  /** Called with the view the operator picked. The app is expected to route to it. */
  onViewChange: (view: BoardView) => void;

  /**
   * Remember the operator's last view across sessions: on mount, a remembered view is
   * re-selected through `onViewChange`.
   *
   * Both sidebars point at the honeycomb's URL, so without this an operator who works from
   * the floor plan would have to toggle to it on every visit. Leave off where the caller pins
   * the view (Multi-Site) — there is nothing to remember.
   */
  rememberView?: boolean;
  /** Scopes what `rememberView` stores. Cloud passes the node id, so each site keeps its own
   *  preference; local has one site and needs no suffix. */
  persistKey?: string;
}

export default function AlertBoardPage({
  alertsApi,
  mapApi,
  geoApi,
  PlayerComponent,
  canEdit = false,
  requireNode = false,
  nodeKey,
  className,
  onViewAlert,
  onOpenRecordings,
  allowedViews = ['hex', 'gis', 'map'],
  singleSite = false,
  view: requested,
  onViewChange,
  rememberView = false,
  persistKey,
}: AlertBoardPageProps) {
  // A geographic board with no api to read is not a board — drop it rather than mount a
  // page that can only render an error.
  const views = (allowedViews.length ? allowedViews : (['hex'] as BoardView[])).filter(
    (v) => v !== 'map' || !!geoApi,
  );
  const storageKey = rememberView ? `${STORAGE_KEY}${persistKey ? `.${persistKey}` : ''}` : null;

  // A view the caller no longer allows (permissions changed, or a deep link into the other
  // board) falls back rather than rendering a board this user must not see. The app wrappers
  // apply the same fallback to the URL, so the address bar doesn't end up naming a board that
  // isn't on screen.
  const view = views.includes(requested) ? requested : views[0];

  const setView = useCallback(
    (next: BoardView) => {
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, next);
        } catch {
          /* private mode — the choice just won't survive a reload */
        }
      }
      onViewChange(next);
    },
    [onViewChange, storageKey],
  );

  // Restore the remembered view once per storage key — so on the way in, and again when the
  // key changes under us (cloud scopes it per node, so switching site restores the view that
  // site was last watched in). Not on every render: after the restore the operator's clicks
  // are the source of truth, and re-running would keep dragging them back.
  const restoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storageKey || restoredForRef.current === storageKey) return;
    restoredForRef.current = storageKey;
    try {
      const saved = localStorage.getItem(storageKey) as BoardView | null;
      if (saved && saved !== view && views.includes(saved)) onViewChange(saved);
    } catch {
      /* private mode — nothing was ever remembered */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Flipping views mid-edit would unmount the board being edited and drop the unsaved pins,
  // so the toggle locks until the operator saves or cancels. Both spatial boards report
  // through the same flag — only one of them is ever mounted.
  const [mapEditing, setMapEditing] = useState(false);
  const onEditingChange = useCallback((editing: boolean) => setMapEditing(editing), []);
  // The board is what owns this state; leaving it can't leave the lock behind.
  useEffect(() => {
    if (view === 'hex') setMapEditing(false);
  }, [view]);

  const headerSlot = useMemo(
    () =>
      views.length < 2 ? null : (
        <SegmentedToggle<BoardView>
          aria-label="Board view"
          options={views.map((v) => VIEW_OPTIONS[v])}
          value={view}
          onChange={setView}
          disabled={mapEditing}
          disabledTitle="Save or cancel the layout first"
        />
      ),
    // `views` is a fresh array each render; its CONTENT is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [views.join(','), view, setView, mapEditing],
  );

  if (view === 'map' && geoApi) {
    return (
      <CameraGeoMapPage
        api={geoApi}
        alertsApi={alertsApi}
        PlayerComponent={PlayerComponent}
        canEdit={canEdit}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={className}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        headerSlot={headerSlot}
        onEditingChange={onEditingChange}
      />
    );
  }

  if (view === 'gis') {
    return (
      <CameraMapPage
        api={mapApi}
        alertsApi={alertsApi}
        PlayerComponent={PlayerComponent}
        canEdit={canEdit}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={className}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        headerSlot={headerSlot}
        onEditingChange={onEditingChange}
      />
    );
  }

  return (
    <AlertHexagonPage
      api={alertsApi}
      PlayerComponent={PlayerComponent}
      requireNode={requireNode}
      nodeKey={nodeKey}
      className={className}
      onViewAlert={onViewAlert}
      onOpenRecordings={onOpenRecordings}
      headerSlot={headerSlot}
      singleSite={singleSite}
    />
  );
}
